import { isRecord } from './common';

/** Bedrockの数値方向。0〜5は下・上・北・南・西・東の順。 */
const FACING_DIRECTION = ['down', 'up', 'north', 'south', 'west', 'east'];
/** 階段の`weirdo_direction`。 */
const WEIRDO_DIRECTION = ['east', 'west', 'south', 'north'];
/** ドアなどの`direction`。 */
const CARDINAL_DIRECTION = ['east', 'south', 'west', 'north'];
/** 塀の面ごとの接続。Javaは`none`/`low`/`tall`で持つ。 */
const WALL_CONNECTIONS: Record<string, string> = { none: 'none', short: 'low', tall: 'tall' };
const SIDES = ['north', 'south', 'east', 'west'];

const colorNames: Record<string, string> = {
  silver: 'light_gray',
};

const woodNames: Record<string, string> = {
  big_oak: 'dark_oak',
};

const stoneTypes: Record<string, string> = {
  stone: 'stone',
  granite: 'granite',
  granite_smooth: 'polished_granite',
  diorite: 'diorite',
  diorite_smooth: 'polished_diorite',
  andesite: 'andesite',
  andesite_smooth: 'polished_andesite',
};

const stoneBrickTypes: Record<string, string> = {
  default: 'stone_bricks',
  mossy: 'mossy_stone_bricks',
  cracked: 'cracked_stone_bricks',
  chiseled: 'chiseled_stone_bricks',
  smooth: 'smooth_stone',
};

const sandstoneTypes: Record<string, string> = {
  default: 'sandstone',
  heiroglyphs: 'chiseled_sandstone',
  cut: 'cut_sandstone',
  smooth: 'smooth_sandstone',
};

const stoneSlabTypes: Record<string, string> = {
  stone: 'stone_slab',
  smooth_stone: 'smooth_stone_slab',
  sandstone: 'sandstone_slab',
  cobblestone: 'cobblestone_slab',
  brick: 'brick_slab',
  stone_brick: 'stone_brick_slab',
  quartz: 'quartz_slab',
  nether_brick: 'nether_brick_slab',
  wood: 'oak_slab',
};

const stoneSlabTypes2: Record<string, string> = {
  red_sandstone: 'red_sandstone_slab',
  purpur: 'purpur_slab',
  prismarine_rough: 'prismarine_slab',
  prismarine_dark: 'dark_prismarine_slab',
  prismarine_brick: 'prismarine_brick_slab',
  mossy_cobblestone: 'mossy_cobblestone_slab',
  smooth_sandstone: 'smooth_sandstone_slab',
  red_nether_brick: 'red_nether_brick_slab',
};

const stoneSlabTypes3: Record<string, string> = {
  end_stone_brick: 'end_stone_brick_slab',
  smooth_red_sandstone: 'smooth_red_sandstone_slab',
  polished_andesite: 'polished_andesite_slab',
  andesite: 'andesite_slab',
  diorite: 'diorite_slab',
  polished_diorite: 'polished_diorite_slab',
  granite: 'granite_slab',
  polished_granite: 'polished_granite_slab',
};

const stoneSlabTypes4: Record<string, string> = {
  mossy_stone_brick: 'mossy_stone_brick_slab',
  smooth_quartz: 'smooth_quartz_slab',
  stone: 'stone_slab',
  cut_sandstone: 'cut_sandstone_slab',
  cut_red_sandstone: 'cut_red_sandstone_slab',
};

/**
 * 平坦化前の石ハーフブロック。種類が多すぎて1つの状態に収まらず、統合版は
 * `stone_slab`〜`stone_block_slab4`の4系統に分けて持っている。系統ごとに表が違う。
 */
const STONE_SLAB = /^(double_)?stone_(?:block_)?slab([234])?$/;

const stoneSlabTables: Record<string, { state: string; types: Record<string, string>; fallback: string }> = {
  '': { state: 'stone_slab_type', types: stoneSlabTypes, fallback: 'stone_slab' },
  '2': { state: 'stone_slab_type_2', types: stoneSlabTypes2, fallback: 'red_sandstone_slab' },
  '3': { state: 'stone_slab_type_3', types: stoneSlabTypes3, fallback: 'end_stone_brick_slab' },
  '4': { state: 'stone_slab_type_4', types: stoneSlabTypes4, fallback: 'mossy_stone_brick_slab' },
};

/** 状態を持たない、単純な名前の置き換え。 */
const directRenames: Record<string, string> = {
  pistonarmcollision: 'piston_head', stickypistonarmcollision: 'piston_head',
  mob_spawner: 'spawner', portal: 'nether_portal',
  invisiblebedrock: 'barrier',
  grass_path: 'dirt_path', stonecutter_block: 'stonecutter',
  movingblock: 'moving_piston', sealantern: 'sea_lantern', undyed_shulker_box: 'shulker_box',
  silver_glazed_terracotta: 'light_gray_glazed_terracotta', standing_banner: 'white_banner', wall_banner: 'white_wall_banner',
  prismarine_bricks_stairs: 'prismarine_brick_stairs', end_brick_stairs: 'end_stone_brick_stairs',
  darkoak_wall_sign: 'dark_oak_wall_sign', darkoak_standing_sign: 'dark_oak_sign',
  lit_smoker: 'smoker', lit_blast_furnace: 'blast_furnace', light_block: 'light', chain: 'iron_chain',
  small_dripleaf_block: 'small_dripleaf', dirt_with_roots: 'rooted_dirt', azalea_leaves_flowered: 'flowering_azalea_leaves',
  cave_vines_body_with_berries: 'cave_vines_plant', cave_vines_head_with_berries: 'cave_vines',
  glow_frame: 'glow_item_frame', waxed_copper: 'waxed_copper_block', frog_spawn: 'frogspawn',
  normal_stone_slab: 'stone_slab', deprecated_anvil: 'anvil',
  deprecated_purpur_block_1: 'purpur_block', deprecated_purpur_block_2: 'purpur_pillar',

  grass: 'grass_block',
  tallgrass: 'short_grass',
  double_plant: 'tall_grass',
  waterlily: 'lily_pad',
  flowing_water: 'water',
  flowing_lava: 'lava',
  lit_pumpkin: 'jack_o_lantern',
  unlit_redstone_torch: 'redstone_torch',
  unpowered_comparator: 'comparator',
  unpowered_repeater: 'repeater',
  wooden_door: 'oak_door',
  wooden_pressure_plate: 'oak_pressure_plate',
  wooden_button: 'oak_button',
  hardened_clay: 'terracotta',
  stone_stairs: 'cobblestone_stairs',
  normal_stone_stairs: 'stone_stairs',
  monster_egg: 'infested_stone',
  web: 'cobweb',
  snow_layer: 'snow',
  snow: 'snow_block',
  brick_block: 'bricks',
  nether_brick: 'nether_bricks',
  red_nether_brick: 'red_nether_bricks',
  end_bricks: 'end_stone_bricks',
  quartz_ore: 'nether_quartz_ore',
  lit_furnace: 'furnace',
  invisible_bedrock: 'barrier',
  frame: 'item_frame',
  trip_wire: 'tripwire',
  wall_sign: 'oak_wall_sign',
  standing_sign: 'oak_sign',
  // オーク製のものだけ接頭辞が無い。`_trapdoor`/`_fence_gate`の判定に載せるため補う。
  trapdoor: 'oak_trapdoor',
  fence_gate: 'oak_fence_gate',
  reeds: 'sugar_cane',
  deadbush: 'dead_bush',
  beetroot: 'beetroots',
  melon_block: 'melon',
  magma: 'magma_block',
  slime: 'slime_block',
  // レッドストーン装置。Bedrockは点灯や伸長で別IDになるものがある。
  // 伸びたピストンの腕。BedrockのIDは `_arm_collision`（`_collector` は存在しない）。
  piston_arm_collision: 'piston_head',
  sticky_piston_arm_collision: 'sticky_piston_head',
  powered_repeater: 'repeater',
  powered_comparator: 'comparator',
  lit_redstone_lamp: 'redstone_lamp',
  lit_redstone_ore: 'redstone_ore',
  lit_deepslate_redstone_ore: 'deepslate_redstone_ore',
  golden_rail: 'powered_rail',
  noteblock: 'note_block',
  daylight_detector_inverted: 'daylight_detector',
};

/** `__proto__`のような名前でプロトタイプ側の値を拾わないよう、自前のキーだけを引く。 */
function lookup<T>(table: Record<string, T>, key: string | undefined): T | undefined {
  return key !== undefined && Object.hasOwn(table, key) ? table[key] : undefined;
}

function pick(states: Record<string, string>, key: string): string | undefined {
  const value = states[key];
  if (value === undefined) return undefined;
  delete states[key];
  return value;
}

function colorOf(states: Record<string, string>): string | undefined {
  const color = pick(states, 'color');
  if (color === undefined) return undefined;
  return lookup(colorNames, color) ?? color;
}

function woodOf(states: Record<string, string>, key: string): string | undefined {
  const wood = pick(states, key);
  if (wood === undefined) return undefined;
  return lookup(woodNames, wood) ?? wood;
}

/** 平坦化前の名前（`wool` + `color=red`など）を、現在のブロックIDへ寄せる。 */
function renameLegacy(name: string, states: Record<string, string>): string {
  const stoneSlab = STONE_SLAB.exec(name);
  if (stoneSlab) {
    const table = stoneSlabTables[stoneSlab[2] ?? ''];
    const type = pick(states, table.state);
    if (stoneSlab[1]) states.type = 'double';
    return lookup(table.types, type) ?? table.fallback;
  }

  switch (name) {
    case 'yellow_flower': return 'dandelion';
    case 'red_flower': return ({poppy:'poppy',orchid:'blue_orchid',allium:'allium',houstonia:'azure_bluet',tulip_red:'red_tulip',tulip_orange:'orange_tulip',tulip_white:'white_tulip',tulip_pink:'pink_tulip',oxeye:'oxeye_daisy',cornflower:'cornflower',lily_of_the_valley:'lily_of_the_valley'} as Record<string,string>)[states.flower_type] ?? 'poppy';
    case 'coral': case 'coral_fan': case 'coral_fan_dead': case 'coral_block': case 'coral_fan_hang': case 'coral_fan_hang2': case 'coral_fan_hang3': {
      const species=({blue:'tube',pink:'brain',purple:'bubble',red:'fire',yellow:'horn'} as Record<string,string>)[states.coral_color] ?? 'tube';
      return `${states.dead_bit === 'true' || name === 'coral_fan_dead' ? 'dead_' : ''}${species}_${name === 'coral_block' ? 'coral_block' : name.includes('hang') ? 'coral_wall_fan' : name.includes('fan') ? 'coral_fan' : 'coral'}`;
    }
    case 'skull': return ['skeleton_skull','wither_skeleton_skull','zombie_head','player_head','creeper_head','dragon_head','piglin_head'][Number(states.skull_type ?? 0)] ?? 'skeleton_skull';
    case 'wool':
    case 'carpet':
    case 'concrete':
    case 'stained_glass':
    case 'stained_glass_pane':
    case 'shulker_box': {
      const color = colorOf(states);
      return color ? `${color}_${name}` : name;
    }
    case 'concretepowder':
    case 'concretePowder':
    case 'concrete_powder': {
      const color = colorOf(states);
      return color ? `${color}_concrete_powder` : 'white_concrete_powder';
    }
    case 'stained_hardened_clay': {
      const color = colorOf(states);
      return color ? `${color}_terracotta` : 'terracotta';
    }
    case 'planks':
    case 'wooden_slab':
    case 'fence': {
      const wood = woodOf(states, 'wood_type');
      if (!wood) return name === 'planks' ? 'oak_planks' : name;
      return name === 'planks' ? `${wood}_planks` : `${wood}_${name.replace('wooden_', '')}`;
    }
    case 'double_wooden_slab': {
      const wood = woodOf(states, 'wood_type') ?? 'oak';
      states.type = 'double';
      return `${wood}_slab`;
    }
    case 'wood': {
      const wood = woodOf(states, 'wood_type') ?? 'oak';
      // 樹皮のある原木ブロック。皮を剥いだものは別IDになる。
      return pick(states, 'stripped_bit') === 'true' ? `stripped_${wood}_wood` : `${wood}_wood`;
    }
    case 'log':
    case 'log2': {
      const wood = woodOf(states, name === 'log' ? 'old_log_type' : 'new_log_type');
      return wood ? `${wood}_log` : 'oak_log';
    }
    case 'leaves':
    case 'leaves2': {
      const wood = woodOf(states, name === 'leaves' ? 'old_leaf_type' : 'new_leaf_type');
      return wood ? `${wood}_leaves` : 'oak_leaves';
    }
    case 'sapling': {
      const wood = woodOf(states, 'sapling_type');
      return wood ? `${wood}_sapling` : 'oak_sapling';
    }
    case 'stone': {
      const type = pick(states, 'stone_type');
      return lookup(stoneTypes, type) ?? 'stone';
    }
    case 'stonebrick': {
      const type = pick(states, 'stone_brick_type');
      return lookup(stoneBrickTypes, type) ?? 'stone_bricks';
    }
    case 'cobblestone_wall': {
      const type = pick(states, 'wall_block_type');
      return type ? `${type === 'cobblestone' ? 'cobblestone' : type}_wall` : 'cobblestone_wall';
    }
    case 'sand': {
      const type = pick(states, 'sand_type');
      return type === 'red' ? 'red_sand' : 'sand';
    }
    case 'sandstone':
    case 'red_sandstone': {
      const type = pick(states, 'sand_stone_type');
      const resolved = lookup(sandstoneTypes, type) ?? 'sandstone';
      return name === 'red_sandstone' ? resolved.replace('sandstone', 'red_sandstone') : resolved;
    }
    case 'prismarine': {
      const type = pick(states, 'prismarine_block_type');
      if (type === 'bricks') return 'prismarine_bricks';
      if (type === 'dark') return 'dark_prismarine';
      return 'prismarine';
    }
    case 'quartz_block': {
      const type = pick(states, 'chisel_type');
      if (type === 'chiseled') return 'chiseled_quartz_block';
      if (type === 'lines') return 'quartz_pillar';
      if (type === 'smooth') return 'smooth_quartz';
      return 'quartz_block';
    }
    case 'anvil': {
      const damage = pick(states, 'damage');
      if (damage === 'slightly_damaged') return 'chipped_anvil';
      if (damage === 'very_damaged') return 'damaged_anvil';
      return 'anvil';
    }
    default:
      return lookup(directRenames, name) ?? name;
  }
}

function convertStates(name: string, states: Record<string, string>): Record<string, string> {
  const properties: Record<string, string> = {};

  const assign = (key: string, value: string | undefined) => {
    if (value !== undefined) properties[key] = value;
  };

  // renameLegacy が二重ハーフブロックと判断したものを先に取り出す。
  assign('type', pick(states, 'type'));

  /** 向きは番号で来ることも、1.20.10以降のように`up`のような語で来ることもある。 */
  const direction = (value: string | undefined, table: string[]): string | undefined => {
    if (value === undefined) return undefined;
    const index = Number(value);
    if (Number.isInteger(index) && index >= 0 && index < table.length) return table[index];
    return table.includes(value) ? value : undefined;
  };

  assign('facing', direction(pick(states, 'weirdo_direction'), WEIRDO_DIRECTION));
  assign('facing', direction(pick(states, 'facing_direction'), FACING_DIRECTION) ?? properties.facing);
  assign('facing', direction(pick(states, 'block_face'), FACING_DIRECTION) ?? properties.facing);
  assign('facing', direction(pick(states, 'direction'), CARDINAL_DIRECTION) ?? properties.facing);
  assign('facing', pick(states, 'cardinal_direction') ?? properties.facing);

  const upsideDown = pick(states, 'upside_down_bit');
  if (upsideDown !== undefined) properties.half = upsideDown === 'true' ? 'top' : 'bottom';
  const upperBlock = pick(states, 'upper_block_bit');
  if (upperBlock !== undefined) properties.half = upperBlock === 'true' ? 'upper' : 'lower';

  const topSlot = pick(states, 'top_slot_bit');
  if (topSlot !== undefined && properties.type !== 'double') properties.type = topSlot === 'true' ? 'top' : 'bottom';
  const verticalHalf = pick(states, 'vertical_half');
  if (verticalHalf !== undefined && properties.type !== 'double') properties.type = verticalHalf;

  // 統合版の塀は面ごとの接続を別々の状態で持つ。捨てるとつながらない柱だけになる。
  SIDES.forEach((side) => {
    const connection = pick(states, `wall_connection_type_${side}`);
    if (connection !== undefined) assign(side, lookup(WALL_CONNECTIONS, connection) ?? connection);
  });
  assign('up', pick(states, 'wall_post_bit'));

  const hinge = pick(states, 'door_hinge_bit');
  if (hinge !== undefined) properties.hinge = hinge === 'true' ? 'right' : 'left';

  assign('axis', pick(states, 'pillar_axis'));
  assign('open', pick(states, 'open_bit'));
  // レッドストーンの強さは Bedrock が redstone_signal、Java が power。
  assign('power', pick(states, 'redstone_signal'));
  // 壁付きの松明は取り付け先の面を持つ。Javaのfacing（頭の向く先）とは意味が逆なので別名で残す。
  assign('attached_face', pick(states, 'torch_facing_direction'));
  assign('persistent', pick(states, 'persistent_bit'));
  assign('hanging', pick(states, 'hanging'));
  assign('age', pick(states, 'growth'));
  if (name === 'snow') {
    const height = pick(states, 'height');
    assign('layers', height === undefined ? undefined : String(Number(height) + 1));
  }

  // 残った状態はそのまま渡す。描画に使わないものも情報として表示できる。
  Object.entries(states).forEach(([key, value]) => {
    if (!Object.hasOwn(properties, key)) properties[key] = value;
  });
  return properties;
}

function stripNamespace(name: string): string {
  const separator = name.indexOf(':');
  return (separator >= 0 ? name.slice(separator + 1) : name).toLowerCase();
}

/** `_bit`で終わる状態などはBedrockの真偽値なので、Javaと同じ`true`/`false`にする。 */
const booleanStates = new Set(['hanging', 'drag_down', 'stripped_bit', 'extinguished']);

function stateToString(key: string, value: unknown): string {
  if (typeof value === 'number' && (key.endsWith('_bit') || booleanStates.has(key))) {
    return value === 0 ? 'false' : 'true';
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

/**
 * `.mcstructure`のブロックを、Java版と同じ名前・プロパティに寄せる。
 * 平坦化前の名前（`wool` + `color=red`）や、Bedrock固有の状態名を吸収する。
 */
export function convertBedrockBlock(rawName: string, rawStates: unknown): { name: string; properties: Record<string, string> } {
  const states: Record<string, string> = {};
  if (isRecord(rawStates)) {
    Object.entries(rawStates).forEach(([key, value]) => {
      // 1.20.10以降は`minecraft:facing_direction`のように名前空間付きの状態が増えた。
      // 接頭辞を剥いでから既存の表に通す。名前空間なしが既にあればそちらを優先する。
      const normalized = stripNamespace(key);
      if (states[normalized] === undefined) states[normalized] = stateToString(normalized, value);
    });
  }

  if (rawName.includes(':') && !rawName.startsWith('minecraft:')) return { name: rawName, properties: states };
  const original = stripNamespace(rawName);
  if (['wool','carpet','stained_glass','stained_glass_pane','concrete','concretepowder','bed'].includes(original)) states.color ??= 'white';
  if (['fence','wooden_slab'].includes(original)) states.wood_type ??= 'oak';
  let name = renameLegacy(original, states);
  // `oak_double_slab`のような平坦化後の二重ハーフブロック。
  if (/_double_slab$/.test(name)) {
    name = name.replace('_double_slab', '_slab');
    states.type = 'double';
  }

  name = name.replace(/_standing_sign$/, '_sign');
  if(name==='normal_stone_slab')name='stone_slab';
  if (/^light_block_\d+$/.test(name)) name = 'light';
  if (name.includes('double_cut_copper_slab')) { name = name.replace('double_cut_copper_slab','cut_copper_slab'); states.type='double'; }
  const properties = convertStates(name, states);
  if (original.includes('with_berries')) properties.berries = 'true';
  if (original === 'stickypistonarmcollision') properties.type = 'sticky';
  if (original === 'pistonarmcollision') properties.type = 'normal';
  if (name === 'bed') name = `${states.color ?? 'white'}_bed`;
  if (name === 'torch' || name === 'redstone_torch' || name === 'soul_torch') {
    const attached=states.torch_facing_direction;
    const opposite: Record<string,string>={north:'south',south:'north',east:'west',west:'east'};
    if(attached && opposite[attached]) { name=name.replace('torch','wall_torch'); properties.facing=opposite[attached]; }
  }
  if (/_slab$/.test(name) && /double/.test(original)) properties.type='double';
  if (states.ground_sign_direction !== undefined) properties.rotation=states.ground_sign_direction;
  if (states.head_piece_bit !== undefined) properties.part=states.head_piece_bit==='true'?'head':'foot';
  if (states.button_pressed_bit !== undefined) properties.powered=states.button_pressed_bit;
  if (/_button$/.test(name)) {
    properties.face=properties.facing==='up'?'floor':properties.facing==='down'?'ceiling':'wall';
    if(properties.facing==='up'||properties.facing==='down')properties.facing='north';
  }
  if (name==='lever' && states.lever_direction) {
    const direction=states.lever_direction;
    properties.face=direction.startsWith('up_')?'floor':direction.startsWith('down_')?'ceiling':'wall';
    properties.facing=direction.includes('east_west')?'east':direction.includes('north_south')?'north':direction;
    properties.powered=states.open_bit??'false';
  }
  if (states.in_wall_bit !== undefined) properties.in_wall=states.in_wall_bit;
  if (/_pressure_plate$/.test(name) && states.redstone_signal !== undefined) properties.powered=Number(states.redstone_signal)>0?'true':'false';
  if (states.repeater_delay !== undefined) properties.delay=String(Number(states.repeater_delay)+1);
  if (states.output_subtract_bit !== undefined) properties.mode=states.output_subtract_bit==='true'?'subtract':'compare';
  if (original.startsWith('powered_')) properties.powered='true';
  if (original.startsWith('unpowered_')) properties.powered='false';
  if (original==='redstone_torch') properties.lit='true';
  if ((name==='campfire'||name==='soul_campfire')&&states.extinguished!==undefined)properties.lit=states.extinguished==='true'?'false':'true';
  if (states.rail_direction !== undefined) properties.shape=['north_south','east_west','ascending_east','ascending_west','ascending_north','ascending_south','south_east','south_west','north_west','north_east'][Number(states.rail_direction)] ?? 'north_south';
  if (states.liquid_depth !== undefined) properties.level=states.liquid_depth;
  if (states.upper_block_bit !== undefined && /_trapdoor$/.test(name)) properties.half=states.upper_block_bit==='true'?'top':'bottom';
  // Bedrockは点灯・消灯で別IDになる。Javaと同じく lit プロパティとして持たせる。
  if (original.startsWith('unlit_')) properties.lit ??= 'false';
  else if (original.startsWith('lit_') && name !== 'jack_o_lantern') properties.lit ??= 'true';

  return { name: `minecraft:${name}`, properties };
}
